<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SettingsFieldSection from 'next/Settings/SettingsFieldSection.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
  errors: {
    type: Object,
    default: () => ({ metrics: {}, dimensions: {} }),
  },
});

const emit = defineEmits(['update:config']);

const { t } = useI18n();

const METRIC_SEPARATOR = '·';

const AGGREGATION_KEYS = {
  count: 'COUNT',
  sum: 'SUM',
  avg: 'AVG',
  min: 'MIN',
  max: 'MAX',
};
const BUILT_IN_DIMENSION_KEYS = {
  user: 'AGENT',
  agent: 'AGENT',
  inbox: 'INBOX',
  contact: 'CONTACT',
  conversation: 'CONVERSATION',
};
const VISUALIZATION_KEYS = {
  bar_chart: 'BAR_CHART',
  line_chart: 'LINE_CHART',
  pie_chart: 'PIE_CHART',
  table: 'TABLE',
};
const GRANULARITY_KEYS = {
  none: 'NONE',
  hour: 'HOUR',
  day: 'DAY',
  week: 'WEEK',
  month: 'MONTH',
  year: 'YEAR',
};
const VALUE_MODE_KEYS = {
  absolute: 'ABSOLUTE',
  percentage: 'PERCENTAGE',
};

const metrics = computed(() => props.config?.metrics || []);
const dimensions = computed(() => props.config?.dimensions || []);
const eventName = computed(() => props.config?.event_name || '');

function aggregationLabel(aggregation) {
  const key = AGGREGATION_KEYS[aggregation];
  return key
    ? // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      t(`WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.AGGREGATION_OPTIONS.${key}`)
    : aggregation;
}

const visualization = computed(() => {
  const key = VISUALIZATION_KEYS[props.config?.default_visualization];
  return key
    ? // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      t(`WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE_OPTIONS.${key}`)
    : props.config?.default_visualization || '';
});

const granularity = computed(() => {
  const key = GRANULARITY_KEYS[props.config?.default_time_granularity] || 'DAY';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(`WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.TIME_GRANULARITY_OPTIONS.${key}`);
});

const valueMode = computed(() => {
  const key = VALUE_MODE_KEYS[props.config?.default_value_mode] || 'ABSOLUTE';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(`WGPT_CUSTOM_REPORTS.VALUE_MODES.${key}`);
});

const summaryCards = computed(() =>
  props.config?.show_summary_cards
    ? t('WGPT_CUSTOM_REPORTS.SUMMARY.SHOWN')
    : t('WGPT_CUSTOM_REPORTS.SUMMARY.HIDDEN')
);

function formatMetricLabel(metric) {
  const aggregation = aggregationLabel(metric.aggregation);
  if (metric.aggregation === 'count') {
    return t('WGPT_CUSTOM_REPORTS.SUMMARY.METRIC_COUNT_LABEL', { aggregation });
  }
  return t('WGPT_CUSTOM_REPORTS.SUMMARY.METRIC_OF_PROPERTY', {
    aggregation,
    property: metric.property || metric.key,
  });
}

function formatDimensionLabel(dim) {
  if (dim.label) return dim.label;
  const key = BUILT_IN_DIMENSION_KEYS[dim.source];
  if (key) {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return t(`WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.BUILT_IN_DIMENSIONS.${key}`);
  }
  return dim.property || dim.source || '';
}

const defaultGroupBy = computed(() => {
  const key = props.config?.default_group_by;
  if (!key) return t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.NONE');

  const dimension = dimensions.value.find(
    dim => dim.property === key || dim.source === key
  );
  if (dimension) return formatDimensionLabel(dimension);

  const builtInKey = BUILT_IN_DIMENSION_KEYS[key];
  if (builtInKey) {
    const path = `WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.BUILT_IN_DIMENSIONS.${builtInKey}`;
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return t(path);
  }
  return key;
});

function removeMetric(index) {
  const updated = metrics.value.filter((_, i) => i !== index);
  emit('update:config', { ...props.config, metrics: updated });
}

function removeDimension(index) {
  const updated = dimensions.value.filter((_, i) => i !== index);
  emit('update:config', { ...props.config, dimensions: updated });
}
</script>

<template>
  <div>
    <SettingsFieldSection
      v-if="eventName"
      :label="t('WGPT_CUSTOM_REPORTS.SUMMARY.EVENT')"
    >
      <span
        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-n-brand/10 text-xs font-medium text-n-brand"
      >
        <span class="i-lucide-tag w-3 h-3" />
        {{ eventName }}
      </span>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('WGPT_CUSTOM_REPORTS.SUMMARY.WHAT_TO_MEASURE')"
    >
      <div v-if="metrics.length" class="flex flex-wrap gap-2">
        <span
          v-for="(metric, index) in metrics"
          :key="metric.key || index"
          v-tooltip="
            errors.metrics[index]
              ? {
                  content: t('WGPT_CUSTOM_REPORTS.VALIDATION.UNKNOWN_PROPERTY'),
                  delay: { show: 300, hide: 0 },
                }
              : undefined
          "
          class="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-xs group"
          :class="
            errors.metrics[index]
              ? 'bg-n-ruby-2 text-n-ruby-11'
              : 'bg-n-blue-2 text-n-blue-11'
          "
        >
          <span
            class="w-3 h-3 shrink-0"
            :class="
              errors.metrics[index]
                ? 'i-lucide-alert-triangle'
                : 'i-lucide-bar-chart-3'
            "
          />
          <span class="font-medium">
            {{ metric.label || formatMetricLabel(metric) }}
          </span>
          <span
            class="mx-0.5"
            :class="errors.metrics[index] ? 'text-n-ruby-8' : 'text-n-blue-8'"
          >
            {{ METRIC_SEPARATOR }}
          </span>
          <span
            :class="errors.metrics[index] ? 'text-n-ruby-9' : 'text-n-blue-9'"
          >
            {{ aggregationLabel(metric.aggregation) }}
          </span>
          <button
            class="ml-0.5 p-0.5 rounded-full transition-colors opacity-0 group-hover:opacity-100"
            :class="
              errors.metrics[index] ? 'hover:bg-n-ruby-4' : 'hover:bg-n-blue-4'
            "
            @click="removeMetric(index)"
          >
            <span class="i-lucide-x w-3 h-3" />
          </button>
        </span>
      </div>
      <p v-else class="text-xs text-n-slate-9 italic">
        {{ t('WGPT_CUSTOM_REPORTS.SUMMARY.NO_METRICS') }}
      </p>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('WGPT_CUSTOM_REPORTS.SUMMARY.HOW_TO_BREAK_DOWN')"
    >
      <div v-if="dimensions.length" class="flex flex-wrap gap-2">
        <span
          v-for="(dim, index) in dimensions"
          :key="dim.property || dim.source || index"
          v-tooltip="
            errors.dimensions[index]
              ? {
                  content: t('WGPT_CUSTOM_REPORTS.VALIDATION.UNKNOWN_PROPERTY'),
                  delay: { show: 300, hide: 0 },
                }
              : undefined
          "
          class="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-xs group"
          :class="
            errors.dimensions[index]
              ? 'bg-n-ruby-2 text-n-ruby-11'
              : 'bg-n-violet-2 text-n-violet-11'
          "
        >
          <span
            class="w-3 h-3 shrink-0"
            :class="
              errors.dimensions[index]
                ? 'i-lucide-alert-triangle'
                : 'i-lucide-layers'
            "
          />
          <span class="font-medium">
            {{ formatDimensionLabel(dim) }}
          </span>
          <button
            class="ml-0.5 p-0.5 rounded-full transition-colors opacity-0 group-hover:opacity-100"
            :class="
              errors.dimensions[index]
                ? 'hover:bg-n-ruby-4'
                : 'hover:bg-n-violet-4'
            "
            @click="removeDimension(index)"
          >
            <span class="i-lucide-x w-3 h-3" />
          </button>
        </span>
      </div>
      <p v-else class="text-xs text-n-slate-9 italic">
        {{ t('WGPT_CUSTOM_REPORTS.SUMMARY.NO_DIMENSIONS') }}
      </p>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE')"
    >
      <span class="flex items-center gap-1 text-sm text-n-slate-12">
        <span class="i-lucide-bar-chart w-4 h-4 text-n-slate-9" />
        {{ visualization }}
      </span>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.TIME_GRANULARITY')"
    >
      <span class="flex items-center gap-1 text-sm text-n-slate-12">
        <span class="i-lucide-calendar w-4 h-4 text-n-slate-9" />
        {{ granularity }}
      </span>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.DEFAULT_GROUP_BY')"
    >
      <span class="flex items-center gap-1 text-sm text-n-slate-12">
        <span class="i-lucide-layers w-4 h-4 text-n-slate-9" />
        {{ defaultGroupBy }}
      </span>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.VALUE_MODE')"
    >
      <span class="flex items-center gap-1 text-sm text-n-slate-12">
        <span class="i-lucide-percent w-4 h-4 text-n-slate-9" />
        {{ valueMode }}
      </span>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.SHOW_SUMMARY_CARDS')"
    >
      <span class="flex items-center gap-1 text-sm text-n-slate-12">
        <span class="i-lucide-layout-dashboard w-4 h-4 text-n-slate-9" />
        {{ summaryCards }}
      </span>
    </SettingsFieldSection>
  </div>
</template>
